import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';

export interface ContainerLabelData {
  code: string;
  copies: number;
}

export class ContainerLabelPDFGenerator {
  private doc: jsPDF;
  // 修改为40mm宽，20mm高
  private readonly LABEL_WIDTH_MM = 40;
  private readonly LABEL_HEIGHT_MM = 20;
  private readonly MARGIN_MM = 2;
  private readonly FONT_SIZE = 8;
  private readonly TITLE_FONT_SIZE = 6;
  private readonly BARCODE_HEIGHT_MM = 6;
  private readonly BARCODE_WIDTH_MM = 30;

  constructor() {
    // 创建一个自定义页面大小的 PDF (40mm宽 × 20mm高)，方向为横向
    this.doc = new jsPDF({
      orientation: 'landscape', // 将方向改为横向
      unit: 'mm',
      format: [40, 20] // 宽度40mm，高度20mm
    });
  }

  /**
   * 生成容器标签 PDF
   * @param containers 容器数据数组
   * @returns Promise<Blob> PDF 文件的 Blob 对象
   */
  public async generateContainerLabels(containers: ContainerLabelData[]): Promise<Blob> {
    let pageNumber = 0;
    const totalPages = containers.reduce((sum, container) => sum + container.copies, 0);

    // 遍历所有容器
    for (const container of containers) {
      // 为每个容器生成指定数量的标签
      for (let copy = 1; copy <= container.copies; copy++) {
        // 如果不是第一页，则添加新页面
        if (pageNumber > 0) {
          this.doc.addPage([40, 20], 'landscape'); // 添加新的自定义大小页面，方向为横向
        }
        pageNumber++;

        // 绘制单个标签（始终在页面的相同位置）
        await this.drawLabel(container.code, this.MARGIN_MM, this.MARGIN_MM, copy, container.copies);
      }
    }

    // 生成 PDF Blob
    const pdfBlob = this.doc.output('blob');
    return pdfBlob;
  }

  /**
   * 绘制单个标签
   * @param containerCode 容器编码
   * @param x X 坐标
   * @param y Y 坐标
   * @param copyNumber 当前份数
   * @param totalCopies 总份数
   */
  private async drawLabel(containerCode: string, x: number, y: number, copyNumber: number, totalCopies: number): Promise<void> {
    // 绘制边框
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.2);
    this.doc.rect(x, y, this.LABEL_WIDTH_MM - 2*this.MARGIN_MM, this.LABEL_HEIGHT_MM - 2*this.MARGIN_MM);

    // 设置字体
    this.doc.setFont('helvetica', 'normal');

    // 绘制标题
    this.doc.setFontSize(this.TITLE_FONT_SIZE);
    this.doc.setTextColor(100, 100, 100);
    const titleText = 'Container';
    const titleWidth = this.doc.getTextWidth(titleText);
    const titleX = x + (this.LABEL_WIDTH_MM - 2*this.MARGIN_MM - titleWidth) / 2;
    this.doc.text(titleText, titleX, y + 3);

    // 绘制容器编码（在标题下方）
    this.doc.setFontSize(this.FONT_SIZE);
    this.doc.setTextColor(0, 0, 0);
    this.doc.setFont('helvetica', 'bold');
    
    // 如果编码过长，截断并添加省略号
    let displayCode = containerCode;
    const maxWidth = this.LABEL_WIDTH_MM - 2*this.MARGIN_MM - 4;
    if (this.doc.getTextWidth(displayCode) > maxWidth) {
      while (this.doc.getTextWidth(displayCode + '...') > maxWidth && displayCode.length > 0) {
        displayCode = displayCode.substring(0, displayCode.length - 1);
      }
      displayCode += '...';
    }
    
    const codeWidth = this.doc.getTextWidth(displayCode);
    const codeX = x + (this.LABEL_WIDTH_MM - 2*this.MARGIN_MM - codeWidth) / 2;
    const codeY = y + 6;
    
    this.doc.text(displayCode, codeX, codeY);

    // 生成并添加Code128条码（在下方）
    try {
      const barcodeDataUrl = await this.generateBarcode(containerCode);
      
      // 计算条码位置（居中）
      const barcodeX = x + (this.LABEL_WIDTH_MM - 2*this.MARGIN_MM - this.BARCODE_WIDTH_MM) / 2;
      const barcodeY = y + 8;
      
      // 添加条码到PDF
      this.doc.addImage(
        barcodeDataUrl, 
        'PNG', 
        barcodeX, 
        barcodeY, 
        this.BARCODE_WIDTH_MM, 
        this.BARCODE_HEIGHT_MM
      );
    } catch (error) {
      console.error('Failed to generate barcode:', error);
      // 如果条码生成失败，显示错误信息
      this.doc.setFontSize(4);
      this.doc.setTextColor(255, 0, 0);
      this.doc.text('Barcode Error', x + 1, y + 10);
    }

    // 绘制份数信息（如果有多份）
    if (totalCopies > 1) {
      this.doc.setFontSize(5);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(150, 150, 150);
      const copyText = `${copyNumber}/${totalCopies}`;
      const copyWidth = this.doc.getTextWidth(copyText);
      const copyX = x + (this.LABEL_WIDTH_MM - 2*this.MARGIN_MM - copyWidth) - 1;
      const copyY = y + this.LABEL_HEIGHT_MM - 2*this.MARGIN_MM - 1;
      this.doc.text(copyText, copyX, copyY);
    }

    // 绘制生成时间（缩小字体）
    this.doc.setFontSize(4);
    this.doc.setTextColor(180, 180, 180);
    const timestamp = new Date().toLocaleDateString('zh-CN');
    this.doc.text(timestamp, x + 1, y + this.LABEL_HEIGHT_MM - 2*this.MARGIN_MM - 1);
  }

  /**
   * 生成Code128条码
   * @param text 要编码的文本
   * @returns Promise<string> 条码图像的DataURL
   */
  private async generateBarcode(text: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // 创建一个临时的canvas元素
        const canvas = document.createElement('canvas');
        
        // 设置适合的尺寸（按照mm转换为px，假设300dpi）
        const dpi = 300;
        const mmToPx = dpi / 25.4;
        canvas.width = this.BARCODE_WIDTH_MM * mmToPx;
        canvas.height = this.BARCODE_HEIGHT_MM * mmToPx;
        
        // 生成Code128条码
        JsBarcode(canvas, text, {
          format: 'CODE128',
          width: 1.5,
          height: 30,
          displayValue: false, // 不显示文本，因为我们已经在PDF中显示了
          margin: 0,
          background: '#ffffff',
          lineColor: '#000000'
        });
        
        // 转换为DataURL
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 下载生成的 PDF
   * @param containers 容器数据数组
   * @param filename 文件名（可选）
   */
  public async downloadContainerLabels(containers: ContainerLabelData[], filename?: string): Promise<void> {
    const pdfBlob = await this.generateContainerLabels(containers);
    
    // 创建下载链接
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `container-labels-${new Date().getTime()}.pdf`;
    
    // 触发下载
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 清理 URL 对象
    URL.revokeObjectURL(url);
  }
}

// 导出一个便捷的函数
export const generateContainerLabelsPDF = async (containers: ContainerLabelData[], filename?: string): Promise<void> => {
  const generator = new ContainerLabelPDFGenerator();
  await generator.downloadContainerLabels(containers, filename);
};